import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router';
import { toast } from 'react-toastify';
import { fetchOrderById, submitFeedback, submitItemAcceptance, submitDoorPreference } from '../../store/slices/ordersSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SellerIdentityRow from '../../components/items/SellerIdentityRow';
import styled from 'styled-components';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import {
  getOrderLineDetails,
  getOrderLineImageUrl,
  getOrderLineKindLabel,
  getOrderLinePath,
  getOrderLineTitle,
} from '../../utils/orderLineDisplay';
import { formatPaymentStatusLabel, getOrderTotals } from '../../utils/orderPaymentDisplay';
import {
  canBuyerDecideOnItem,
  canBuyerStateDoorPreference,
  canRateOrderLine,
  canShowOrderItemFeedback,
  formatAcceptancePolicyLabel,
  formatAcceptanceStatusLabel,
  formatBuyerDoorPreferenceLabel,
  formatDecisionDeadline,
  formatMatchesDescriptionLabel,
  hasBuyerDoorPreference,
  formatOrderStatusLabel,
  getOrderDisplayId,
  getOrderStatusTextColor,
} from '../../utils/orderStatusDisplay';
import {
  formatDeliveryStatusLabel,
  isShipmentDelivered,
  getDeliveryStatusBadgeColor,
} from '../../utils/shipmentStatusDisplay';

const DetailContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const DetailCard = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 2rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
  }
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ItemCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 1.5rem;
  background-color: var(--bg-secondary);
`;

const ItemRow = styled.div`
  display: flex;
  gap: 1.25rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ItemImage = styled(Link)`
  width: 140px;
  height: 140px;
  flex-shrink: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 220px;
  }
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const KindLabel = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(props) => (props.$boutique ? '#b45309' : '#7c3aed')};
  margin-bottom: 0.35rem;
`;

const ItemTitle = styled(Link)`
  display: block;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
  text-decoration: none;

  &:hover {
    color: var(--primary-color);
  }
`;

const ItemPrice = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const ItemDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0 0 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ItemMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
`;

const ItemDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FeedbackPrompt = styled.div`
  margin: 1.5rem 0 0.5rem;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  background: #fffbf0;
  border: 1px solid #e8c547;
  color: var(--text-primary);
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const FeedbackCard = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  background-color: ${props => props.$done ? 'var(--bg-primary)' : '#fffbf0'};
  border-radius: 0.5rem;
  border: 1px solid ${props => props.$done ? 'var(--border-color)' : '#e8c547'};
`;

const DoorPreferenceCard = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  background-color: ${props => props.$done ? 'var(--bg-primary)' : '#f0f7ff'};
  border-radius: 0.5rem;
  border: 1px solid ${props => props.$done ? 'var(--border-color)' : '#b8d4f0'};
`;

const InfoNote = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0 0 0.75rem;
  padding: 0.75rem 0.9rem;
  background: ${props => (props.$subtle ? 'transparent' : 'rgba(255,255,255,0.65)')};
  border-radius: 0.375rem;
`;

const TextLinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.5rem;
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  &:hover { opacity: 0.85; }
`;

const FeedbackTextarea = styled.textarea`
  width: 100%;
  min-height: 70px;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.95rem;
  resize: vertical;
  margin-bottom: 0.75rem;
  &:focus { outline: none; border-color: var(--primary-color); }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const SubmitButton = styled.button`
  padding: 0.65rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  background-color: var(--primary-color);
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ChoiceButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: 1px solid ${(props) => (props.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${(props) => (props.$active ? 'var(--primary-color)' : 'white')};
  color: ${(props) => (props.$active ? 'white' : 'var(--text-primary)')};
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DoneText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const BackButton = styled.button`
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: background-color 0.2s ease;
  &:hover { background-color: var(--bg-tertiary); }
`;

const DeliveryBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: ${props => (props.$color ? `${props.$color}22` : props.$delivered ? '#d4edda' : '#fff3cd')};
  color: ${props => props.$color || (props.$delivered ? '#155724' : '#856404')};
`;

const OrderDetailPage = () => {
  const { orderRef } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOrder, loading, error } = useSelector((state) => state.orders);
  const [itemAsDescribedStates, setItemAsDescribedStates] = useState({});
  const [sellerResponsivenessStates, setSellerResponsivenessStates] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [acceptanceSubmitting, setAcceptanceSubmitting] = useState({});
  const [doorPreferenceSubmitting, setDoorPreferenceSubmitting] = useState({});
  const [doorPreferenceEditing, setDoorPreferenceEditing] = useState({});
  const [doorRejectionReasonStates, setDoorRejectionReasonStates] = useState({});
  const [matchesDescriptionStates, setMatchesDescriptionStates] = useState({});
  const [rejectionReasonStates, setRejectionReasonStates] = useState({});
  const itemAsDescribedLabel = (value) => {
    const labels = {
      exactly_as_described: 'Exactly as described',
      minor_differences: 'Minor differences',
      very_different: 'Very different',
    };
    return labels[value] || 'N/A';
  };
  const responsivenessLabel = (value) => {
    const labels = { yes: 'Yes', average: 'Average', no: 'No' };
    return labels[value] || 'N/A';
  };

  useEffect(() => {
    if (orderRef) {
      dispatch(fetchOrderById(orderRef));
    }
  }, [orderRef, dispatch]);

  const handleSubmitFeedback = async (orderItemId) => {
    const itemAsDescribed = itemAsDescribedStates[orderItemId];
    const sellerResponsiveness = sellerResponsivenessStates[orderItemId];
    if (!itemAsDescribed) {
      toast.error('Please answer whether the item was as described.');
      return;
    }
    if (!sellerResponsiveness) {
      toast.error('Please answer whether the seller was responsive.');
      return;
    }
    setSubmitting(prev => ({ ...prev, [orderItemId]: true }));
    try {
      await dispatch(submitFeedback({
        orderItemId,
        itemAsDescribed,
        sellerResponsiveness,
      })).unwrap();
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error(err || 'Failed to submit feedback.');
    } finally {
      setSubmitting(prev => ({ ...prev, [orderItemId]: false }));
    }
  };

  const handleAcceptance = async (orderItemId, accepted) => {
    const matchesDescription = matchesDescriptionStates[orderItemId];
    if (matchesDescription !== true && matchesDescription !== false) {
      toast.error('Please answer whether the item matches its description.');
      return;
    }
    const rejectionReason = (rejectionReasonStates[orderItemId] || '').trim();
    if (!accepted && rejectionReason.length < 3) {
      toast.error('Please provide a reason for returning this item.');
      return;
    }

    setAcceptanceSubmitting((prev) => ({ ...prev, [orderItemId]: true }));
    try {
      await dispatch(submitItemAcceptance({
        orderItemId,
        accepted,
        matchesDescription,
        rejectionReason: accepted ? undefined : rejectionReason,
        orderId: currentOrder?.order_id || null,
      })).unwrap();
      toast.success(accepted ? 'Item accepted — thank you!' : 'Item rejected. We will process your return.');
      setMatchesDescriptionStates((prev) => {
        const next = { ...prev };
        delete next[orderItemId];
        return next;
      });
      setRejectionReasonStates((prev) => {
        const next = { ...prev };
        delete next[orderItemId];
        return next;
      });
    } catch (err) {
      toast.error(err || 'Could not submit your decision.');
    } finally {
      setAcceptanceSubmitting((prev) => ({ ...prev, [orderItemId]: false }));
    }
  };

  const handleDoorPreference = async (orderItemId, accepted) => {
    const rejectionReason = (doorRejectionReasonStates[orderItemId] || '').trim();
    if (!accepted && rejectionReason.length < 3) {
      toast.error('Please tell us why you returned this item.');
      return;
    }

    setDoorPreferenceSubmitting((prev) => ({ ...prev, [orderItemId]: true }));
    try {
      await dispatch(submitDoorPreference({
        orderItemId,
        accepted,
        rejectionReason: accepted ? undefined : rejectionReason,
        orderId: currentOrder?.order_id || orderRef || null,
      })).unwrap();
      toast.success(accepted ? 'Thanks — we noted you kept this item.' : 'Thanks — we noted you returned this item.');
      setDoorPreferenceEditing((prev) => {
        const next = { ...prev };
        delete next[orderItemId];
        return next;
      });
      setDoorRejectionReasonStates((prev) => {
        const next = { ...prev };
        delete next[orderItemId];
        return next;
      });
    } catch (err) {
      toast.error(err || 'Could not save your decision.');
    } finally {
      setDoorPreferenceSubmitting((prev) => ({ ...prev, [orderItemId]: false }));
    }
  };

  if (loading && !currentOrder) {
    return <LoadingSpinner text="Loading order details..." />;
  }

  if (error) {
    return (
      <DetailContainer>
        <DetailCard>
          <p style={{ color: 'var(--error-color)', textAlign: 'center' }}>
            {error || 'Failed to load order details'}
          </p>
        </DetailCard>
      </DetailContainer>
    );
  }

  if (!currentOrder) {
    return (
      <DetailContainer>
        <DetailCard>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            Order not found
          </p>
        </DetailCard>
      </DetailContainer>
    );
  }

  const isDelivered = isShipmentDelivered(currentOrder.delivery_status);
  const canLeaveFeedback = canShowOrderItemFeedback(currentOrder, currentOrder.delivery_status);
  const deliveryLabel = formatDeliveryStatusLabel(currentOrder.delivery_status);
  const { itemsSubtotal, fees, orderTotal } = getOrderTotals(currentOrder);
  const orderItems = currentOrder.items || [];
  const linesAwaitingRating = orderItems.filter(
    (item) => canRateOrderLine(item, currentOrder, currentOrder.delivery_status) && !item.feedback
  );

  return (
    <DetailContainer>
      <BackButton onClick={() => navigate('/orders')}>
        ← Back to Orders
      </BackButton>

      <DetailCard>
        <PageTitle>{getOrderDisplayId(currentOrder)}</PageTitle>

        <OrderInfo>
          <InfoItem>
            <InfoLabel>Order Date</InfoLabel>
            <InfoValue>
              {new Date(currentOrder.created_at || currentOrder.order_id).toLocaleDateString()}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Status</InfoLabel>
            <InfoValue style={{
              color: getOrderStatusTextColor(currentOrder.status) || 'var(--text-primary)',
            }}>
              {formatOrderStatusLabel(currentOrder.status)}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Delivery</InfoLabel>
            <InfoValue>
              <DeliveryBadge $delivered={isDelivered} $color={getDeliveryStatusBadgeColor(currentOrder.delivery_status)}>
                {deliveryLabel}
              </DeliveryBadge>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Payment method</InfoLabel>
            <InfoValue>{currentOrder.payment_method_label || 'Instapay'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Payment status</InfoLabel>
            <InfoValue>{formatPaymentStatusLabel(currentOrder.payment_status)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Items subtotal</InfoLabel>
            <InfoValue>{formatItemPriceEgp(itemsSubtotal)}</InfoValue>
          </InfoItem>
          {fees != null ? (
            <InfoItem>
              <InfoLabel>Delivery &amp; service fees</InfoLabel>
              <InfoValue>{formatItemPriceEgp(fees)}</InfoValue>
            </InfoItem>
          ) : null}
          <InfoItem>
            <InfoLabel>Order total</InfoLabel>
            <InfoValue>{formatItemPriceEgp(orderTotal)}</InfoValue>
          </InfoItem>
          {currentOrder.use_wallet_for_points ? (
            <InfoItem>
              <InfoLabel>Wallet</InfoLabel>
              <InfoValue>Used for item payment</InfoValue>
            </InfoItem>
          ) : null}
          {Number(currentOrder.wallet_applied_to_fees_egp) > 0 ? (
            <InfoItem>
              <InfoLabel>Wallet applied to fees</InfoLabel>
              <InfoValue>{formatItemPriceEgp(currentOrder.wallet_applied_to_fees_egp)}</InfoValue>
            </InfoItem>
          ) : null}
          {Number(currentOrder.amount_paid_externally_egp) > 0 ? (
            <InfoItem>
              <InfoLabel>Paid via {currentOrder.payment_method_label || 'Instapay'}</InfoLabel>
              <InfoValue>{formatItemPriceEgp(currentOrder.amount_paid_externally_egp)}</InfoValue>
            </InfoItem>
          ) : null}
        </OrderInfo>

        {linesAwaitingRating.length > 0 && (
          <FeedbackPrompt>
            <strong>Share your purchase feedback.</strong>{' '}
            Scroll to each item below and answer the two quick feedback questions.
          </FeedbackPrompt>
        )}

        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
          Items
        </h2>

        <ItemsList>
          {orderItems.length > 0 ? (
            orderItems.map((item) => {
              const orderItemId = item.order_item_id || item.id || item.orderItemId;
              if (!orderItemId) return null;

              const existingFeedback = item.feedback;
              const hasFeedback = !!existingFeedback;
              const canRateLine = canRateOrderLine(item, currentOrder, currentOrder.delivery_status);
              const isItemSubmitting = submitting[orderItemId] || false;
              const selectedItemAsDescribed = itemAsDescribedStates[orderItemId] || '';
              const selectedSellerResponsiveness = sellerResponsivenessStates[orderItemId] || '';

              const itemDetails = getOrderLineDetails(item);
              const itemTitle = getOrderLineTitle(item);
              const itemImage = getOrderLineImageUrl(item);
              const itemPath = getOrderLinePath(item);
              const kindLabel = getOrderLineKindLabel(item);
              const unitPrice = item.price || item.price_at_time || itemDetails.price || 0;
              const quantity = item.quantity || 1;

              return (
                <ItemCard key={orderItemId}>
                  <ItemRow>
                    <ItemImage to={itemPath} state={{ itemId: item.listing_id }}>
                      {itemImage ? (
                        <img src={itemImage} alt={itemTitle} />
                      ) : (
                        <span style={{ fontSize: '2.5rem' }}>📦</span>
                      )}
                    </ItemImage>

                    <ItemBody>
                      {kindLabel ? (
                        <KindLabel $boutique={item.line_kind === 'boutique'}>{kindLabel}</KindLabel>
                      ) : null}
                      {itemDetails.brand_name ? (
                        <ItemMeta style={{ marginBottom: '0.35rem', fontWeight: 600 }}>
                          {itemDetails.brand_name}
                        </ItemMeta>
                      ) : null}
                      <ItemTitle to={itemPath} state={{ itemId: item.listing_id }}>
                        {itemTitle}
                      </ItemTitle>
                      <ItemPrice>{formatItemPriceEgp(unitPrice)}</ItemPrice>
                      {itemDetails.description ? (
                        <ItemDescription>{itemDetails.description}</ItemDescription>
                      ) : null}
                      <ItemMeta>
                        {itemDetails.category ? <span>{itemDetails.category}</span> : null}
                        {itemDetails.item_code ? (
                          <span style={{ fontFamily: 'monospace' }}>{itemDetails.item_code}</span>
                        ) : null}
                      </ItemMeta>
                      {(itemDetails.seller_display_name || itemDetails.seller_display_username) ? (
                        <SellerIdentityRow
                          name={itemDetails.seller_display_name}
                          username={itemDetails.seller_display_username}
                          profilePicture={itemDetails.seller_profile_picture}
                        />
                      ) : null}
                    </ItemBody>
                  </ItemRow>

                  <ItemDetails>
                    <InfoItem>
                      <InfoLabel>Quantity</InfoLabel>
                      <InfoValue>{quantity}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Price per item</InfoLabel>
                      <InfoValue>{formatItemPriceEgp(unitPrice)}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Line total</InfoLabel>
                      <InfoValue>{formatItemPriceEgp(Number(unitPrice) * quantity)}</InfoValue>
                    </InfoItem>
                  </ItemDetails>

                  {canBuyerDecideOnItem(item, currentOrder) && (
                    <FeedbackCard $done={false}>
                      <p style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
                        Try at home — accept or reject this item
                      </p>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                        {formatAcceptancePolicyLabel(item.acceptance_policy)}
                        {item.buyer_decision_required_until ? (
                          <> · Please decide by {formatDecisionDeadline(item.buyer_decision_required_until)}</>
                        ) : null}
                      </p>
                      <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                        Does this item match its description?
                      </p>
                      <ActionButtons style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                        <ChoiceButton
                          type="button"
                          $active={matchesDescriptionStates[orderItemId] === true}
                          disabled={acceptanceSubmitting[orderItemId]}
                          onClick={() => setMatchesDescriptionStates((prev) => ({
                            ...prev,
                            [orderItemId]: true,
                          }))}
                        >
                          Yes
                        </ChoiceButton>
                        <ChoiceButton
                          type="button"
                          $active={matchesDescriptionStates[orderItemId] === false}
                          disabled={acceptanceSubmitting[orderItemId]}
                          onClick={() => setMatchesDescriptionStates((prev) => ({
                            ...prev,
                            [orderItemId]: false,
                          }))}
                        >
                          No
                        </ChoiceButton>
                      </ActionButtons>
                      <p style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
                        Reason for return (required if you reject)
                      </p>
                      <FeedbackTextarea
                        placeholder="Tell us why you are returning this item..."
                        value={rejectionReasonStates[orderItemId] || ''}
                        onChange={(e) => setRejectionReasonStates((prev) => ({
                          ...prev,
                          [orderItemId]: e.target.value,
                        }))}
                        disabled={acceptanceSubmitting[orderItemId]}
                      />
                      <ActionButtons style={{ marginTop: 0 }}>
                        <SubmitButton
                          type="button"
                          disabled={acceptanceSubmitting[orderItemId]}
                          onClick={() => handleAcceptance(orderItemId, true)}
                        >
                          {acceptanceSubmitting[orderItemId] ? '…' : 'Keep item'}
                        </SubmitButton>
                        <SubmitButton
                          type="button"
                          style={{ backgroundColor: 'var(--error-color, #dc3545)' }}
                          disabled={acceptanceSubmitting[orderItemId]}
                          onClick={() => handleAcceptance(orderItemId, false)}
                        >
                          Return item
                        </SubmitButton>
                      </ActionButtons>
                    </FeedbackCard>
                  )}

                  {canBuyerStateDoorPreference(item, currentOrder) && (
                    hasBuyerDoorPreference(item) && !doorPreferenceEditing[orderItemId] ? (
                      <DoorPreferenceCard $done>
                        <DoneText>
                          <span>
                            {formatBuyerDoorPreferenceLabel(item.buyer_door_preference)}
                          </span>
                        </DoneText>
                        {item.buyer_door_preference === 'rejected' && item.buyer_door_preference_reason ? (
                          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                            Your reason: {item.buyer_door_preference_reason}
                          </p>
                        ) : null}
                        <TextLinkButton
                          type="button"
                          onClick={() => {
                            setDoorPreferenceEditing((prev) => ({ ...prev, [orderItemId]: true }));
                            setDoorRejectionReasonStates((prev) => ({
                              ...prev,
                              [orderItemId]: item.buyer_door_preference_reason || '',
                            }));
                          }}
                        >
                          Change answer
                        </TextLinkButton>
                      </DoorPreferenceCard>
                    ) : (
                      <DoorPreferenceCard>
                        <p style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
                          Try at door — did you keep or return this item?
                        </p>
                        <InfoNote>
                          Tell us what you decided at the door. This helps us improve your experience.
                          Physical returns are handled by your courier — this form does not process a return on its own.
                        </InfoNote>
                        <p style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
                          Why you returned it (required if you returned the item)
                        </p>
                        <FeedbackTextarea
                          placeholder="e.g. wrong size, not as described, changed my mind..."
                          value={doorRejectionReasonStates[orderItemId] || ''}
                          onChange={(e) => setDoorRejectionReasonStates((prev) => ({
                            ...prev,
                            [orderItemId]: e.target.value,
                          }))}
                          disabled={doorPreferenceSubmitting[orderItemId]}
                        />
                        <ActionButtons style={{ marginTop: 0 }}>
                          <SubmitButton
                            type="button"
                            disabled={doorPreferenceSubmitting[orderItemId]}
                            onClick={() => handleDoorPreference(orderItemId, true)}
                          >
                            {doorPreferenceSubmitting[orderItemId] ? '…' : 'I kept this item'}
                          </SubmitButton>
                          <SubmitButton
                            type="button"
                            style={{ backgroundColor: 'var(--error-color, #dc3545)' }}
                            disabled={doorPreferenceSubmitting[orderItemId]}
                            onClick={() => handleDoorPreference(orderItemId, false)}
                          >
                            I returned this item
                          </SubmitButton>
                        </ActionButtons>
                      </DoorPreferenceCard>
                    )
                  )}

                  {!canBuyerDecideOnItem(item, currentOrder) && item.acceptance_status && item.acceptance_status !== 'pending' && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Item decision: {formatAcceptanceStatusLabel(item.acceptance_status)}
                      </p>
                      {item.rating?.matches_description != null && (
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {formatMatchesDescriptionLabel(item.rating.matches_description)}
                        </p>
                      )}
                      {item.rejection_reason ? (
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          Return reason: {item.rejection_reason}
                        </p>
                      ) : null}
                    </div>
                  )}

                  {canRateLine && (
                    <FeedbackCard $done={hasFeedback}>
                      {hasFeedback ? (
                        <DoneText>
                          <span>
                            Item: {itemAsDescribedLabel(existingFeedback.item_as_described)}
                          </span>
                          <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                            Responsive: {responsivenessLabel(existingFeedback.seller_responsiveness)}
                          </span>
                        </DoneText>
                      ) : (
                        <>
                          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Was the item as described?</p>
                          <ActionButtons style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                            <ChoiceButton
                              type="button"
                              $active={selectedItemAsDescribed === 'exactly_as_described'}
                              disabled={isItemSubmitting}
                              onClick={() => setItemAsDescribedStates((prev) => ({
                                ...prev,
                                [orderItemId]: 'exactly_as_described',
                              }))}
                            >
                              Exactly as described
                            </ChoiceButton>
                            <ChoiceButton
                              type="button"
                              $active={selectedItemAsDescribed === 'minor_differences'}
                              disabled={isItemSubmitting}
                              onClick={() => setItemAsDescribedStates((prev) => ({
                                ...prev,
                                [orderItemId]: 'minor_differences',
                              }))}
                            >
                              Minor differences
                            </ChoiceButton>
                            <ChoiceButton
                              type="button"
                              $active={selectedItemAsDescribed === 'very_different'}
                              disabled={isItemSubmitting}
                              onClick={() => setItemAsDescribedStates((prev) => ({
                                ...prev,
                                [orderItemId]: 'very_different',
                              }))}
                            >
                              Very different
                            </ChoiceButton>
                          </ActionButtons>
                          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Are they responsive?</p>
                          <ActionButtons style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                            <ChoiceButton
                              type="button"
                              $active={selectedSellerResponsiveness === 'yes'}
                              disabled={isItemSubmitting}
                              onClick={() => setSellerResponsivenessStates((prev) => ({
                                ...prev,
                                [orderItemId]: 'yes',
                              }))}
                            >
                              Yes
                            </ChoiceButton>
                            <ChoiceButton
                              type="button"
                              $active={selectedSellerResponsiveness === 'average'}
                              disabled={isItemSubmitting}
                              onClick={() => setSellerResponsivenessStates((prev) => ({
                                ...prev,
                                [orderItemId]: 'average',
                              }))}
                            >
                              Average
                            </ChoiceButton>
                            <ChoiceButton
                              type="button"
                              $active={selectedSellerResponsiveness === 'no'}
                              disabled={isItemSubmitting}
                              onClick={() => setSellerResponsivenessStates((prev) => ({
                                ...prev,
                                [orderItemId]: 'no',
                              }))}
                            >
                              No
                            </ChoiceButton>
                          </ActionButtons>
                          <SubmitButton
                            onClick={() => handleSubmitFeedback(orderItemId)}
                            disabled={isItemSubmitting || !selectedItemAsDescribed || !selectedSellerResponsiveness}
                          >
                            {isItemSubmitting ? 'Submitting...' : 'Submit Feedback'}
                          </SubmitButton>
                        </>
                      )}
                    </FeedbackCard>
                  )}

                  {!canRateLine && item.acceptance_status === 'rejected' && (
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                      This item was rejected — feedback is not collected for returned items.
                    </p>
                  )}

                  {!canRateLine && canLeaveFeedback && item.acceptance_status !== 'rejected' && !hasFeedback && (
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                      Rating is not available for this line yet.
                    </p>
                  )}

                  {!canLeaveFeedback && ['confirmed', 'in_fulfillment', 'awaiting_acceptance', 'settling'].includes(currentOrder.status) && (
                    <FeedbackCard $done={false}>
                      <p style={{ color: '#856404', fontWeight: 500, margin: 0 }}>
                        Feedback will be available once the order is delivered.
                      </p>
                    </FeedbackCard>
                  )}
                </ItemCard>
              );
            })
          ) : (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              {canLeaveFeedback
                ? 'No item lines were returned for this order. Feedback needs order lines in the database — contact support if this looks wrong.'
                : 'No items found in this order'}
            </p>
          )}
        </ItemsList>
      </DetailCard>
    </DetailContainer>
  );
};

export default OrderDetailPage;
